/**
 * @module ExternalLinkHelper
 */
class ExternalLinkHelper {

    /**
     * @param {HTMLElement} element - The HTMLElement this module is constructed upon
     * @param {Object} options - ConditionerJS's merged options
     */
    constructor(element, options) {
        this.element = element;

        /**
         * External links: start with http or https, 2 slashes and some other optional characters, before containing the rest of a (regular) expression
         */
        this.regExBaseString = 'https?://(.*)';

        this.externalLinkRegEx = new RegExp(this.regExBaseString, 'i');

        // perform a limited manual merge, since Conditioner will not deep-merge correctly...
        this._options = this.defaults;
        this._options.signalActivePopup = options.signalActivePopup || this._options.signalActivePopup;
        this._options.excludedLinksRegex = options.excludedLinks && options.excludedLinks !== '' ? new RegExp(`${this.regExBaseString}${options.excludedLinks}`, 'i') : undefined;
        this._options.linkDetection.amgenCommercial.regExString = options.amgenCommercial || this._options.linkDetection.amgenCommercial.regExString;
        this._options.linkDetection.amgenNonCommercial.regExString = options.amgenNonCommercial || this._options.linkDetection.amgenNonCommercial.regExString;

        // pre-create regular expressions. Saves having to create them every time a link has to be checked
        for (let type in this._options.linkDetection) {
            if ({}.hasOwnProperty.call(this._options.linkDetection, type)) {
                this._options.linkDetection[type].regEx = new RegExp(`${this.regExBaseString}${this._options.linkDetection[type].regExString}`, 'i');
            }
        }

        this.init();
    }

    /**
     * @returns {{excludedLinks: string, signalActivePopup: boolean, activePopupClass: string, linkDetection: {amgenCommercial: {regExString: string, className: string}, amgenNonCommercial: {regExString: string, className: string}, otherExternal: {regExString: string, className: string}}}} - return
     */
    get defaults() {
        return {

            /**
             * String for regular expression that marks any links that should NEVER get any dialog at all
             */
            excludedLinks: '',

            /**
             * Whether or not to add a class to the html tag that (for example) handles scrolling while the popup is showing.
             * Add rules to stylesheet to manage this.
             */
            signalActivePopup: false,
            activePopupClass: 'external-link-popup-active',

            /**
             * Object used to determine what kind of link was clicked. The class on the dialog container will determine which content is shown, by styling.
             * Evaluated top-down, picking whatever occurs first!
             */
            linkDetection: {
                amgenCommercial: {

                    /**
                     * Default amgen link: starts with http/https, two slashes, may contain prefixes (such as eu., nl.),
                     * must include ".amgen" or "-amgen" before land code (.com, .nl, .eu, .co.uk, etc)
                     */
                    regExString: '(.+?[\\.-])?amgen\\.[\\w](.*)',
                    className: 'popup-type--amgen-commercial'
                },
                amgenNonCommercial: {
                    regExString: '(.+?[\\.-])?amgen\\.[\\w](.*)',
                    className: 'popup-type--amgen-non-commercial'
                },
                otherExternal: {
                    regExString: '',
                    className: 'popup-type--non-amgen'
                }
            }
        };
    }

    /**
     * init
     */
    init() {
        this.overlay = document.querySelector('[data-module*=ExternalLinkHelper]');

        if (this.overlay) {
            this.dialog = this.overlay.querySelector('.external-link-helper_dialog');
            this.rejectButton = this.dialog.querySelector('.external-link-helper_button--reject');
            this.replacementLink = this.dialog.querySelector('.external-link-helper_button--confirm');

            if (this.rejectButton && this.replacementLink) {
                this.insertHostname();
                this.addEventListeners();
            }
        }
    }

    /**
     * addEventListeners
     */
    addEventListeners() {
        document.documentElement.addEventListener('click', event => this.onDocumentClick(event));
        this.overlay.addEventListener('click', event => this.onRejectDialog(event));
        this.rejectButton.addEventListener('click', event => this.onRejectDialog(event));
        this.replacementLink.addEventListener('click', event => this.onConfirmDialog(event));
    }

    /**
     * Listen to all document clicks and for each click always determine if it might trigger a link.
     * This way we will even catch clicks on links that have been added dynamically later on
     * @param {Event} event - event
     */
    onDocumentClick(event) {
        let link = this.isLinkOrPartOfLink(event.target);
        if (link && this.isExternalLinkInSameWindow(link) && !this.isIgnoredLink(link)) {
            const type = this.getLinkType(link);
            if (type) {
                event.preventDefault();
                this.transferLinkToDialog(link);
                this.showDialog(type);
            }
        }
    }

    /**
     * @param {Event} event - event
     */
    onRejectDialog(event) {
        event.preventDefault();
        // do not bubble up to document again, or we might see the dialog again too
        event.stopPropagation();
        this.hideDialog();
    }

    /**
     * @param {Event} event - event
     */
    onConfirmDialog(event) {
        this.hideDialog();
        event.stopPropagation();
    }

    /**
     * @param {Element} element -element
     * @returns {boolean|Element} returns false when element was not a link or not part of a link. Returns the linking element otherwise.
     */
    isLinkOrPartOfLink(element) {
        const root = document.documentElement;
        while (element.nodeName !== 'A' && element !== root) {
            element = element.parentNode;
        }

        return element !== root ? element : false;
    }

    /**
     * Does this anchor link an external site?
     * Determined by checking start of link (absolute or not) and by looking for the current hostname (for 'confused' harcoded links)
     * @param {HTMLLinkElement} element - element
     * @returns {boolean} boolean - true/false
     */
    isExternalLinkInSameWindow(element) {
        const href = element.href;

        return this.externalLinkRegEx.test(href) && href.indexOf(location.hostname) === -1;
    }

    /**
     * Does this anchor match one of the excluded link patterns? False if no exclusion patterns are defined
     * @param {HTMLLinkElement} element - element
     * @returns {boolean} boolean - true/false
     */
    isIgnoredLink(element) {
        return this._options.excludedLinksRegex ? this._options.excludedLinksRegex.test(element.href) : false;
    }

    /**
     * @param {HTMLLinkElement} link - linkje
     * @returns {*} * - *
     */
    getLinkType(link) {
        for (let type in this._options.linkDetection) {
            if ({}.hasOwnProperty.call(this._options.linkDetection, type)) {
                const typeConfig = this._options.linkDetection[type];
                if (typeConfig.regEx.test(link.href)) {
                    return type;
                }
            }
        }

        return null;
    }

    /**
     * insertHostname - function
     */
    insertHostname() {
        let hostnamePlaceholders = Array.prototype.slice.call(this.dialog.querySelectorAll('.external-link-helper_hostname'));
        hostnamePlaceholders.forEach(placeholder => {
            placeholder.innerHTML = location.hostname;
        });
    }

    /**
     * transferLinkToDialog - function
     * @param {HTMLLinkElement} link - linkje
     */
    transferLinkToDialog(link) {
        this.replacementLink.href = link.href;
        this.replacementLink.target = link.target;
    }

    /**
     * @param {String} forLinkType, matching one of the propertynames found in @see options.linkDetection
     */
    showDialog(forLinkType) {
        // clear out old link type classes.
        for (let type in this._options.linkDetection) {
            if ({}.hasOwnProperty.call(this._options.linkDetection, type)) {
                this.element.classList.remove(this._options.linkDetection[type].className);
            }
        }

        // set the actual type. css should handle the rest
        this.element.classList.add(this._options.linkDetection[forLinkType].className);

        if (this._options.signalActivePopup) {
            document.documentElement.classList.add(this._options.activePopupClass);
        }
        this.overlay.setAttribute('role', 'alert');
        this.overlay.classList.remove('hidden');
        this.overlay.classList.add('visible');
    }

    /**
     * hideDialog - function
     */
    hideDialog() {
        this.overlay.classList.remove('visible');
        this.overlay.classList.add('hidden');
        this.overlay.removeAttribute('role');

        if (this._options.signalActivePopup) {
            document.documentElement.classList.remove(this._options.activePopupClass);
        }
    }
}

export default ExternalLinkHelper;
